import { createElement, ReactElement, useEffect, useMemo, useRef } from "react";

import kanaExpressionInterpreter from "../../../lib/KanaExpressionInterpreter";
import { RepeaterData } from "../../../models";
import { logError } from "../../../service/error";
import { getSelectDataFromStringArray } from "../../../utils";
import { ReadonlyText } from "../../data";
import { FormLabel } from "../FormLabel";
import { DatePicker, TextInput } from "../Input";
import { MultiSelect } from "../MultiSelect";
import { ReadonlyTableRepeater, TableRepeater } from "../Repeater";
import { Select } from "../Select";
import { KanaTooltip } from "../Tooltip";
import {
  DynamicFieldValueMapperArgs,
  DynamicFormFieldsFromJsonSchemaProps,
  DynamicJsonSchemaProperty,
  DynamicReadonlyValueFormatterArgs,
  DynamicRenderedField,
  DynamicSchemaProperties,
} from "./models";
import {
  getDateFromValue,
  isArrayEnumField,
  isDateField,
  isNumberField,
  isObjectField,
  isParagraphField,
  isTableRepeaterField,
  normaliseExpressionContextValue,
  normaliseSchemaFieldLabel,
} from "./utils";

const buildSelectDataFromEnumValues = (
  enumValues: Array<string | number>,
  enumNames?: string[]
): { key: string | number; value: string | number | null }[] => {
  return enumValues.map((enumValue, index) => ({
    key: enumValue,
    value: enumNames?.[index] ?? enumValue,
  }));
};

const getEnumValueFromSelectValue = (
  selectedValue: string,
  enumValues: Array<string | number>
): string | number | undefined => {
  return enumValues.find((enumValue) => String(enumValue) === selectedValue);
};

const getEnumValuesFromSelectValues = (
  selectedValues: string[],
  enumValues: Array<string | number>
): Array<string | number> =>
  selectedValues.map((selectedValue) => getEnumValueFromSelectValue(selectedValue, enumValues) ?? selectedValue);

const getSchemaPropertyEntries = <TProperty extends DynamicJsonSchemaProperty>(
  schemaProperties: DynamicSchemaProperties<TProperty>
): [string, TProperty][] => {
  if (!schemaProperties) {
    return [];
  }

  return Array.isArray(schemaProperties) ? schemaProperties : Object.entries(schemaProperties);
};

const sortSchemaPropertyEntriesBySequence = <TProperty extends DynamicJsonSchemaProperty>(
  propertyEntries: [string, TProperty][]
): [string, TProperty][] => {
  return propertyEntries
    .map(([fieldKey, fieldDefinition], index) => ({ fieldKey, fieldDefinition, index }))
    .sort((leftEntry, rightEntry) => {
      const leftSequence = leftEntry.fieldDefinition.sequence;
      const rightSequence = rightEntry.fieldDefinition.sequence;

      if (leftSequence === undefined && rightSequence === undefined) {
        return leftEntry.index - rightEntry.index;
      }

      if (leftSequence === undefined) {
        return 1;
      }

      if (rightSequence === undefined) {
        return -1;
      }

      if (leftSequence !== rightSequence) {
        return leftSequence - rightSequence;
      }

      return leftEntry.index - rightEntry.index;
    })
    .map(({ fieldKey, fieldDefinition }) => [fieldKey, fieldDefinition]);
};

const mapFieldValue = <TProperty extends DynamicJsonSchemaProperty, TValue>(
  mapper: ((args: DynamicFieldValueMapperArgs<TProperty, TValue>) => unknown) | undefined,
  args: DynamicFieldValueMapperArgs<TProperty, TValue>
): unknown => {
  return mapper ? mapper(args) : args.value;
};

const getEnumDisplayValue = (
  value: string | number,
  enumValues: Array<string | number>,
  enumNames?: string[]
): string => {
  const enumIndex = enumValues.findIndex((enumValue) => Object.is(enumValue, value));

  if (enumIndex === -1) {
    return String(value);
  }

  return String(enumNames?.[enumIndex] ?? enumValues[enumIndex]);
};

const getDefaultReadonlyValue = <TProperty extends DynamicJsonSchemaProperty>({
  fieldDefinition,
  fieldValue,
}: DynamicReadonlyValueFormatterArgs<TProperty>): string | undefined => {
  if (isObjectField(fieldDefinition) || isTableRepeaterField(fieldDefinition)) {
    return undefined;
  }

  if (fieldDefinition.enum && fieldValue !== undefined && fieldValue !== null && fieldValue !== "") {
    return getEnumDisplayValue(fieldValue as string | number, fieldDefinition.enum, fieldDefinition.enumNames);
  }

  if (Array.isArray(fieldValue)) {
    if (fieldDefinition.items?.enum) {
      return fieldValue.length > 0
        ? fieldValue
            .map((arrayValue) =>
              getEnumDisplayValue(
                arrayValue as string | number,
                fieldDefinition.items?.enum as Array<string | number>,
                fieldDefinition.items?.enumNames
              )
            )
            .join(", ")
        : undefined;
    }

    return fieldValue.length > 0 ? fieldValue.map((arrayValue) => String(arrayValue)).join(", ") : undefined;
  }

  if (fieldValue === undefined || fieldValue === null || fieldValue === "") {
    return undefined;
  }

  return String(fieldValue);
};

const areFieldValuesEqual = (currentValue: unknown, defaultValue: unknown): boolean => {
  const leftValueIsEmpty = currentValue === undefined || currentValue === null || currentValue === "";
  const defaultValueIsEmpty = defaultValue === undefined || defaultValue === null || defaultValue === "";

  if (leftValueIsEmpty && defaultValueIsEmpty) {
    return true;
  }

  if (Array.isArray(currentValue) && Array.isArray(defaultValue)) {
    return (
      currentValue.length === defaultValue.length &&
      currentValue.every((arrayValue, index) => Object.is(arrayValue, defaultValue[index]))
    );
  }

  return Object.is(currentValue, defaultValue);
};

const getFieldTooltip = <TProperty extends DynamicJsonSchemaProperty>(
  fieldDefinition: TProperty,
  hasError = false
): JSX.Element | undefined => {
  if (!fieldDefinition.tooltip) {
    return undefined;
  }

  return <KanaTooltip tooltipHeader="" tooltipText={fieldDefinition.tooltip} allowDangerousHtml hasError={hasError} />;
};

const getResolvedNumberInputProps = <TProperty extends DynamicJsonSchemaProperty>(
  fieldKey: string,
  fieldDefinition: TProperty,
  getNumberInputProps: DynamicFormFieldsFromJsonSchemaProps<TProperty>["getNumberInputProps"]
): {
  minNumberValue?: number;
  maxNumberValue?: number;
  decimalPlaces?: number;
} => {
  const numberInputProps = getNumberInputProps ? getNumberInputProps(fieldKey, fieldDefinition) : undefined;

  return {
    minNumberValue: fieldDefinition.minimum ?? fieldDefinition.minNumberValue ?? numberInputProps?.minNumberValue,
    maxNumberValue: fieldDefinition.maximum ?? fieldDefinition.maxNumberValue ?? numberInputProps?.maxNumberValue,
    decimalPlaces: fieldDefinition.decimalPlaces ?? numberInputProps?.decimalPlaces,
  };
};

const applyFieldAffixes = <TProperty extends DynamicJsonSchemaProperty>(
  fieldDefinition: TProperty,
  value: string | undefined
): string | undefined => {
  if (value === undefined) {
    return undefined;
  }

  return `${fieldDefinition.prefix ?? ""}${value}${fieldDefinition.suffix ?? ""}`;
};

const getExpressionContextForEvaluation = <TProperty extends DynamicJsonSchemaProperty>(
  propertyEntries: [string, TProperty][],
  expressionContext: unknown
): unknown => {
  if (!expressionContext || typeof expressionContext !== "object" || Array.isArray(expressionContext)) {
    return expressionContext;
  }

  return propertyEntries.reduce<Record<string, unknown>>(
    (normalisedContext, [fieldKey, fieldDefinition]) => {
      // eslint-disable-next-line no-param-reassign
      normalisedContext[fieldKey] = normaliseExpressionContextValue(
        fieldDefinition,
        (expressionContext as Record<string, unknown>)[fieldKey]
      );
      return normalisedContext;
    },
    { ...(expressionContext as Record<string, unknown>) }
  );
};

const buildNestedFieldPath = (parentFieldKey: string, childFieldKey: string, rowIndex?: number): string => {
  return rowIndex === undefined
    ? `${parentFieldKey}.${childFieldKey}`
    : `${parentFieldKey}[${rowIndex}].${childFieldKey}`;
};

const getTableRepeaterRows = (value: unknown): RepeaterData[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((row): row is RepeaterData => row !== null && typeof row === "object") as RepeaterData[];
};

const getReadonlyDisplayValue = <TProperty extends DynamicJsonSchemaProperty>(
  args: DynamicReadonlyValueFormatterArgs<TProperty>,
  formatReadonlyValue: DynamicFormFieldsFromJsonSchemaProps<TProperty>["formatReadonlyValue"]
): string | undefined => {
  const readonlyValueFromFormatter =
    !(args.fieldDefinition.hasFormatter === false) && formatReadonlyValue ? formatReadonlyValue(args) : undefined;
  const readonlyValue =
    readonlyValueFromFormatter !== undefined ? readonlyValueFromFormatter : getDefaultReadonlyValue(args);

  return applyFieldAffixes(args.fieldDefinition, readonlyValue);
};

const createTableRepeaterChild = (component: unknown, props: Record<string, unknown>): ReactElement => {
  return createElement(component as never, props as never);
};

const DynamicFormFieldsFromJsonSchemaContent = <TProperty extends DynamicJsonSchemaProperty>({
  schemaProperties,
  getFieldValue,
  onFieldChange,
  onFieldsChange,
  expressionContext,
  isReadonly = false,
  isFieldReadonly,
  hasFieldError,
  shouldRenderField,
  getFieldLabel,
  getEnumValues,
  formatReadonlyValue,
  valueMappers,
  parseDateFromValue = getDateFromValue,
  getNumberInputProps,
  renderLayout,
}: DynamicFormFieldsFromJsonSchemaProps<TProperty>): JSX.Element | null => {
  const properties = useMemo(() => getSchemaPropertyEntries(schemaProperties), [schemaProperties]);
  const orderedProperties = useMemo(() => sortSchemaPropertyEntriesBySequence(properties), [properties]);
  const evaluationExpressionContext = useMemo(
    () => getExpressionContextForEvaluation(properties, expressionContext),
    [expressionContext, properties]
  );
  const userEditedFieldKeysRef = useRef<Set<string>>(new Set());

  const handleFieldChange = (fieldKey: string, value: unknown, options?: { isUserEdit?: boolean }): void => {
    if (options?.isUserEdit) {
      userEditedFieldKeysRef.current.add(fieldKey);
    }

    onFieldChange?.(fieldKey, value);
  };

  useEffect(() => {
    if (!onFieldChange || evaluationExpressionContext === undefined || properties.length === 0) {
      return;
    }

    const pendingFieldUpdates: Record<string, unknown> = {};

    properties.forEach(([fieldKey, fieldDefinition]) => {
      if (!fieldDefinition.defaultValueExpression) {
        return;
      }

      const currentFieldValue =
        pendingFieldUpdates[fieldKey] !== undefined ? pendingFieldUpdates[fieldKey] : getFieldValue(fieldKey);
      let defaultValue: unknown;
      try {
        defaultValue = kanaExpressionInterpreter(fieldDefinition.defaultValueExpression, evaluationExpressionContext);
      } catch (e) {
        const errorMessage = `Failed to evaluate defaultValueExpression "${fieldDefinition.defaultValueExpression}": ${e instanceof Error ? e.message : String(e)}`;
        const enrichedError = new Error(errorMessage);
        if (e instanceof Error) enrichedError.stack = e.stack;
        logError({ error: enrichedError });
        return;
      }
      if (defaultValue === undefined) {
        return;
      }

      if (userEditedFieldKeysRef.current.has(fieldKey)) {
        if (
          areFieldValuesEqual(currentFieldValue, defaultValue) ||
          currentFieldValue === undefined ||
          currentFieldValue === null ||
          currentFieldValue === ""
        ) {
          userEditedFieldKeysRef.current.delete(fieldKey);
        } else {
          return;
        }
      }

      if (!areFieldValuesEqual(currentFieldValue, defaultValue)) {
        pendingFieldUpdates[fieldKey] = defaultValue;
      }
    });

    const pendingFieldKeys = Object.keys(pendingFieldUpdates);
    if (pendingFieldKeys.length === 0) {
      return;
    }

    if (onFieldsChange) {
      onFieldsChange(pendingFieldUpdates);
      return;
    }

    pendingFieldKeys.forEach((fieldKey) => {
      handleFieldChange(fieldKey, pendingFieldUpdates[fieldKey]);
    });
  }, [evaluationExpressionContext, getFieldValue, onFieldChange, onFieldsChange, properties]);

  const renderEditableFieldElement = (
    fieldPath: string,
    fieldDefinition: TProperty,
    fieldValue: unknown,
    onChangeValue: (value: unknown) => void,
    label?: string
  ): ReactElement | null => {
    const tooltip = getFieldTooltip(fieldDefinition, hasFieldError?.(fieldPath, fieldDefinition, fieldValue) ?? false);
    const fieldHasError = hasFieldError ? hasFieldError(fieldPath, fieldDefinition, fieldValue) : false;
    const enumValues = getEnumValues ? getEnumValues(fieldPath, fieldDefinition, getFieldValue) : fieldDefinition.enum;

    if (enumValues && enumValues.length > 0) {
      return (
        <Select
          label={label}
          value={fieldValue == null ? undefined : String(fieldValue)}
          data={buildSelectDataFromEnumValues(enumValues, fieldDefinition.enumNames)}
          tooltip={tooltip}
          hasError={fieldHasError}
          onChange={(updatedValue) => {
            const resolvedValue = getEnumValueFromSelectValue(updatedValue, enumValues) ?? updatedValue;
            const mappedValue = mapFieldValue(valueMappers?.select, {
              fieldKey: fieldPath,
              fieldDefinition,
              fieldValue,
              value: resolvedValue,
            });

            onChangeValue(mappedValue);
          }}
        />
      );
    }

    if (isArrayEnumField(fieldDefinition)) {
      const itemEnumValues = fieldDefinition.items?.enum ?? [];

      return (
        <MultiSelect
          label={label}
          data={getSelectDataFromStringArray(itemEnumValues.map((enumValue) => String(enumValue)))}
          values={Array.isArray(fieldValue) ? fieldValue.map((arrayValue) => String(arrayValue)) : undefined}
          tooltip={tooltip}
          hasError={fieldHasError}
          onChange={(updatedValue) => {
            const mappedValue = mapFieldValue(valueMappers?.multiSelect, {
              fieldKey: fieldPath,
              fieldDefinition,
              fieldValue,
              value: updatedValue,
            });

            if (Array.isArray(mappedValue)) {
              onChangeValue(
                getEnumValuesFromSelectValues(
                  mappedValue.map((value) => String(value)),
                  itemEnumValues
                )
              );
              return;
            }

            onChangeValue(mappedValue);
          }}
        />
      );
    }

    if (isDateField(fieldDefinition)) {
      return (
        <DatePicker
          label={label}
          value={parseDateFromValue(fieldValue)}
          tooltip={tooltip}
          hasError={fieldHasError}
          onChange={(updatedValue) => {
            const mappedValue = mapFieldValue(valueMappers?.date, {
              fieldKey: fieldPath,
              fieldDefinition,
              fieldValue,
              value: updatedValue,
            });

            onChangeValue(mappedValue);
          }}
          showFormat={false}
        />
      );
    }

    if (isNumberField(fieldDefinition)) {
      const numberInputProps = getResolvedNumberInputProps(fieldPath, fieldDefinition, getNumberInputProps);

      return (
        <TextInput
          label={label}
          name={fieldPath}
          value={fieldValue == null ? undefined : String(fieldValue)}
          tooltip={tooltip}
          hasError={fieldHasError}
          onChange={(updatedValue) => {
            const mappedValue = mapFieldValue(valueMappers?.number, {
              fieldKey: fieldPath,
              fieldDefinition,
              fieldValue,
              value: updatedValue,
            });

            onChangeValue(mappedValue);
          }}
          type="number"
          decimalPlaces={numberInputProps.decimalPlaces}
          minNumberValue={numberInputProps.minNumberValue}
          maxNumberValue={numberInputProps.maxNumberValue}
          prefix={fieldDefinition.prefix}
          suffix={fieldDefinition.suffix}
        />
      );
    }

    if (isObjectField(fieldDefinition) || isTableRepeaterField(fieldDefinition)) {
      return null;
    }

    return (
      <TextInput
        label={label}
        name={fieldPath}
        value={fieldValue == null ? undefined : String(fieldValue)}
        tooltip={tooltip}
        hasError={fieldHasError}
        onChange={(updatedValue) => {
          const mappedValue = mapFieldValue(valueMappers?.text, {
            fieldKey: fieldPath,
            fieldDefinition,
            fieldValue,
            value: updatedValue,
          });

          onChangeValue(mappedValue);
        }}
        type="text"
        prefix={fieldDefinition.prefix}
        suffix={fieldDefinition.suffix}
      />
    );
  };

  const renderReadonlyFieldElement = (
    fieldPath: string,
    fieldDefinition: TProperty,
    fieldValue: unknown,
    label?: string
  ): ReactElement | null => {
    if (isTableRepeaterField(fieldDefinition)) {
      const rowPropertyEntries = sortSchemaPropertyEntriesBySequence(
        getSchemaPropertyEntries(fieldDefinition.items?.properties as DynamicSchemaProperties<TProperty>)
      );
      const rows = getTableRepeaterRows(fieldValue);
      const tableHeaders = rowPropertyEntries.map(([rowFieldKey, rowFieldDefinition]) =>
        getFieldLabel ? getFieldLabel(rowFieldKey, rowFieldDefinition) : normaliseSchemaFieldLabel(rowFieldKey)
      );

      return (
        <ReadonlyTableRepeater label={label} tableHeaders={tableHeaders}>
          {rows.map((row, rowIndex) => (
            // eslint-disable-next-line react/no-array-index-key
            <tr className="TableRepeaterRow" key={`${fieldPath}_${rowIndex}`}>
              {rowPropertyEntries.map(([rowFieldKey, rowFieldDefinition]) => {
                const rowFieldPath = buildNestedFieldPath(fieldPath, rowFieldKey, rowIndex);
                const rowFieldValue = row[rowFieldKey];
                const readonlyDisplayValue = getReadonlyDisplayValue(
                  {
                    fieldKey: rowFieldPath,
                    fieldDefinition: rowFieldDefinition,
                    fieldValue: rowFieldValue,
                  },
                  formatReadonlyValue
                );

                return <td key={rowFieldPath}>{readonlyDisplayValue ?? "--"}</td>;
              })}
            </tr>
          ))}
        </ReadonlyTableRepeater>
      );
    }

    const readonlyDisplayValue = getReadonlyDisplayValue(
      {
        fieldKey: fieldPath,
        fieldDefinition,
        fieldValue,
      },
      formatReadonlyValue
    );

    if (isObjectField(fieldDefinition)) {
      return null;
    }

    return (
      <ReadonlyText
        label={label}
        value={readonlyDisplayValue}
        tooltipHeader={fieldDefinition.tooltip ? "" : undefined}
        tooltipText={fieldDefinition.tooltip}
        allowTooltipDangerousHtml
      />
    );
  };

  const renderTableRepeaterField = (
    fieldKey: string,
    fieldDefinition: TProperty,
    fieldValue: unknown,
    label: string
  ): ReactElement | null => {
    const rowPropertyEntries = sortSchemaPropertyEntriesBySequence(
      getSchemaPropertyEntries(fieldDefinition.items?.properties as DynamicSchemaProperties<TProperty>)
    );
    if (rowPropertyEntries.length === 0) {
      return null;
    }

    const rows = getTableRepeaterRows(fieldValue);

    if (isReadonly || !onFieldChange) {
      return renderReadonlyFieldElement(fieldKey, fieldDefinition, rows, label);
    }

    const tableHeaders = rowPropertyEntries.map(([rowFieldKey, rowFieldDefinition]) =>
      getFieldLabel ? getFieldLabel(rowFieldKey, rowFieldDefinition) : normaliseSchemaFieldLabel(rowFieldKey)
    );

    const children = rowPropertyEntries.map(([rowFieldKey, rowFieldDefinition]) => {
      const fieldPath = buildNestedFieldPath(fieldKey, rowFieldKey);
      const numberInputProps = getResolvedNumberInputProps(fieldPath, rowFieldDefinition, getNumberInputProps);
      const enumValues = getEnumValues
        ? getEnumValues(fieldPath, rowFieldDefinition, getFieldValue)
        : rowFieldDefinition.enum;
      const mapTextRepeaterValue = (rowFieldValue: string): unknown =>
        mapFieldValue(valueMappers?.text, {
          fieldKey: fieldPath,
          fieldDefinition: rowFieldDefinition,
          fieldValue: undefined,
          value: rowFieldValue,
        });

      const mapNumberRepeaterValue = (rowFieldValue: string): unknown => {
        if (valueMappers?.number) {
          return mapFieldValue(valueMappers.number, {
            fieldKey: fieldPath,
            fieldDefinition: rowFieldDefinition,
            fieldValue: undefined,
            value: rowFieldValue,
          });
        }
        const numericValue = Number(rowFieldValue);
        return Number.isNaN(numericValue) ? rowFieldValue : numericValue;
      };

      const mapDateRepeaterValue = (rowFieldValue: Date | undefined): unknown =>
        mapFieldValue(valueMappers?.date, {
          fieldKey: fieldPath,
          fieldDefinition: rowFieldDefinition,
          fieldValue: undefined,
          value: rowFieldValue,
        });

      const mapSelectRepeaterValue = (rowFieldValue: string): unknown =>
        mapFieldValue(valueMappers?.select, {
          fieldKey: fieldPath,
          fieldDefinition: rowFieldDefinition,
          fieldValue: undefined,
          value: getEnumValueFromSelectValue(rowFieldValue, enumValues ?? []) ?? rowFieldValue,
        });

      if (enumValues && enumValues.length > 0) {
        return createTableRepeaterChild(Select, {
          key: rowFieldKey,
          label: "",
          value: undefined,
          data: buildSelectDataFromEnumValues(enumValues, rowFieldDefinition.enumNames),
          dataType: "String",
          isForTableRepeater: true,
          repeaterRenderProps: { parentIsTableRepeater: true, dataType: "String", componentType: "Select" },
          repeaterTransformSavedValue: (value: string) => mapSelectRepeaterValue(value),
          hasError: false,
          onChange: () => undefined,
        });
      }

      if (isDateField(rowFieldDefinition)) {
        return createTableRepeaterChild(DatePicker, {
          key: rowFieldKey,
          label: "",
          value: undefined,
          showFormat: false,
          dataType: "Date",
          repeaterRenderProps: { parentIsTableRepeater: true, dataType: "Date", componentType: "DatePicker" },
          repeaterTransformSavedValue: (value: Date | undefined) => mapDateRepeaterValue(value),
          hasError: false,
          onChange: () => undefined,
        });
      }

      if (isNumberField(rowFieldDefinition)) {
        return createTableRepeaterChild(TextInput, {
          key: rowFieldKey,
          label: "",
          name: fieldPath,
          value: undefined,
          type: "number",
          dataType: "Number",
          decimalPlaces: numberInputProps.decimalPlaces,
          minNumberValue: numberInputProps.minNumberValue,
          maxNumberValue: numberInputProps.maxNumberValue,
          prefix: rowFieldDefinition.prefix,
          suffix: rowFieldDefinition.suffix,
          repeaterRenderProps: { parentIsTableRepeater: true, dataType: "Number", componentType: "TextInput" },
          repeaterTransformSavedValue: (value: string) => mapNumberRepeaterValue(value),
          hasError: false,
          onChange: () => undefined,
        });
      }

      return createTableRepeaterChild(TextInput, {
        key: rowFieldKey,
        label: "",
        name: fieldPath,
        value: undefined,
        type: "text",
        dataType: "String",
        prefix: rowFieldDefinition.prefix,
        suffix: rowFieldDefinition.suffix,
        repeaterRenderProps: { parentIsTableRepeater: true, dataType: "String", componentType: "TextInput" },
        repeaterTransformSavedValue: (value: string) => mapTextRepeaterValue(value),
        hasError: false,
        onChange: () => undefined,
      });
    });

    return (
      <div>
        <FormLabel label={label} />
        <TableRepeater
          maxRows={1000}
          tableHeaders={tableHeaders}
          initialValue={rows}
          onChange={(updatedRows) => {
            handleFieldChange(fieldKey, updatedRows, { isUserEdit: true });
          }}
        >
          {children}
        </TableRepeater>
        {rows.map((row, rowIndex) =>
          rowPropertyEntries.map(([rowFieldKey, rowFieldDefinition]) => {
            const rowFieldPath = buildNestedFieldPath(fieldKey, rowFieldKey, rowIndex);
            const rowFieldValue = row[rowFieldKey];
            const rowFieldHasError = hasFieldError
              ? hasFieldError(rowFieldPath, rowFieldDefinition, rowFieldValue)
              : false;

            return rowFieldHasError ? (
              <div key={`${rowFieldPath}_error`} style={{ display: "none" }}>
                {rowFieldPath}
              </div>
            ) : null;
          })
        )}
      </div>
    );
  };

  if (properties.length === 0) {
    if (renderLayout) {
      return <>{renderLayout([])}</>;
    }

    return null;
  }

  const renderedFields = orderedProperties
    .filter(([fieldKey, fieldDefinition]) => {
      if (!shouldRenderField) {
        return true;
      }

      return shouldRenderField(fieldKey, fieldDefinition);
    })
    .map(([fieldKey, fieldDefinition]) => {
      const fieldValue = getFieldValue(fieldKey);
      const label = getFieldLabel ? getFieldLabel(fieldKey, fieldDefinition) : normaliseSchemaFieldLabel(fieldKey);

      if (isParagraphField(fieldDefinition)) {
        const htmlContent = typeof fieldDefinition.defaultValue === "string" ? fieldDefinition.defaultValue : "";
        return {
          key: fieldKey,
          fieldDefinition,
          isFullWidth: true,
          // eslint-disable-next-line react/no-danger
          element: <p dangerouslySetInnerHTML={{ __html: htmlContent }} />,
        };
      }

      const fieldIsReadonly = isFieldReadonly ? isFieldReadonly(fieldKey, fieldDefinition, fieldValue) : false;
      const effectiveIsReadonly = isReadonly || !onFieldChange || fieldIsReadonly || fieldDefinition.readOnly === true;

      if (effectiveIsReadonly) {
        const element = renderReadonlyFieldElement(fieldKey, fieldDefinition, fieldValue, label);

        return element
          ? {
              key: fieldKey,
              fieldDefinition,
              isFullWidth: isTableRepeaterField(fieldDefinition) || undefined,
              element,
            }
          : null;
      }

      if (isTableRepeaterField(fieldDefinition)) {
        const element = renderTableRepeaterField(fieldKey, fieldDefinition, fieldValue, label);

        return element
          ? {
              key: fieldKey,
              fieldDefinition,
              isFullWidth: true,
              element,
            }
          : null;
      }

      const element = renderEditableFieldElement(
        fieldKey,
        fieldDefinition,
        fieldValue,
        (updatedValue) => {
          handleFieldChange(fieldKey, updatedValue, { isUserEdit: true });
        },
        label
      );

      if (!element) {
        return null;
      }

      return {
        key: fieldKey,
        fieldDefinition,
        element,
      };
    })
    .filter((value): value is DynamicRenderedField<TProperty> => value !== null);

  if (renderedFields.length === 0) {
    if (renderLayout) {
      return <>{renderLayout([])}</>;
    }

    return null;
  }

  if (renderLayout) {
    return <>{renderLayout(renderedFields)}</>;
  }

  return (
    <>
      {renderedFields.map((field) => (
        <div key={field.key}>{field.element}</div>
      ))}
    </>
  );
};

export const DynamicFormFieldsFromJsonSchema = <TProperty extends DynamicJsonSchemaProperty>(
  props: DynamicFormFieldsFromJsonSchemaProps<TProperty>
): JSX.Element | null => {
  const {
    getFieldValue,
    onFieldChange,
    onFieldsChange,
    isReadonly,
    isFieldReadonly,
    hasFieldError,
    shouldRenderField,
    getFieldLabel,
    getEnumValues,
    formatReadonlyValue,
    valueMappers,
    parseDateFromValue,
    getNumberInputProps,
    renderLayout,
    schemaProperties,
    expressionContext,
  } = props;

  return (
    <DynamicFormFieldsFromJsonSchemaContent
      schemaProperties={schemaProperties}
      getFieldValue={getFieldValue}
      onFieldChange={onFieldChange}
      onFieldsChange={onFieldsChange}
      expressionContext={expressionContext}
      isReadonly={isReadonly}
      isFieldReadonly={isFieldReadonly}
      hasFieldError={hasFieldError}
      shouldRenderField={shouldRenderField}
      getFieldLabel={getFieldLabel}
      getEnumValues={getEnumValues}
      formatReadonlyValue={formatReadonlyValue}
      valueMappers={valueMappers}
      parseDateFromValue={parseDateFromValue}
      getNumberInputProps={getNumberInputProps}
      renderLayout={renderLayout}
    />
  );
};
